import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Home.scss";
import heroImage from "../AboutUs/assets/about-us-cover.webp";
import legoIllustration from "./assets/lego-illustration.webp";
import chipIllustrationOne from "./assets/chip-illustration.webp";
import chipIllustrationTwo from "./assets/chip-illustration-2.webp";
import shieldIllustration from "./assets/shield-illustration.webp";
import industryOne from "./assets/industry-1.webp";
import industryTwo from "./assets/industry-2.webp";
import industryThree from "./assets/industry-3.webp";
import industryFour from "./assets/industry-4.webp";
import successIcon from "./assets/success_icon.svg";
import errorIcon from "./assets/error_icon.svg";
import synopsysLogo from "./assets/synopsys-logo.webp";
import ttLogo from "./assets/tt-logo.webp";
import casLogo from "./assets/cas-logo.webp";
import ieee_logo from "../CoursePage/assets/ieee.webp";
import NavBar from "../../shared/components/NavBar/NavBar";
import Footer from "../../shared/components/Footer/Footer";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Spinner from "../../shared/components/Spinner/Spinner";
import { cardData } from "../../shared/programCatalogData";
import ProgramCard from "../../shared/components/ProgramCard/ProgramCard";

const LEARNOVA_CATALOG = "https://learnova.skillsurf.lk/catalog";

// Program counts are derived from the catalog so they stay accurate as
// programs are added.
const getStats = (courses: ReturnType<typeof cardData>) => ({
  programs: courses.length,
  shortCourses: courses.filter((c) => c.type === "Short Course").length,
  workshops: courses.filter((c) => c.type === "Workshop").length,
  onDemand: courses.filter((c) => c.onDemand).length,
});

// Figures and quotes from the Skill Surf 2025 Impact Report.
const impact = {
  studentsToDate: "1,527",
  avgRating: "4.85",
  enrolledThisYear: "294",
  year: 2025,
  highlights: [
    {
      icon: "bi-cpu",
      title: "First-ever IC tapeout",
      text: "As part of the AICD Student Contest, organized in collaboration with IEEE CASS CEDA Sri Lanka.",
    },
    {
      icon: "bi-trophy",
      title: "Bronze at the Microelectronics Olympiad",
      text: "Rajinthan Rameshkumar won a Bronze Medal at the 20th International Microelectronics Olympiad.",
    },
    {
      icon: "bi-motherboard",
      title: "Build Your Own CPU, completed",
      text: "Learners finished the specialization with a RISC-V single-cycle processor design.",
    },
    {
      icon: "bi-plug",
      title: "Smart plugs delivered",
      text: "Embedded Product Design for IoT '25 learners built and delivered working smart plugs.",
    },
    {
      icon: "bi-mic",
      title: "Industry guest lectures",
      text: "Many guest lectures from professionals in semiconductors, AI and computer architecture.",
    },
    {
      icon: "bi-people",
      title: "FinishingSchool.LK",
      text: "A 3-week interactive soft skills development program with 350+ participants.",
    },
  ],
  feedback: [
    {
      quote: "It was well-organized, informative, and engaging.",
      course: "Art of IC Design '25",
    },
    {
      quote:
        "The teaching was really good. The lecturers knew their stuff and explained things clearly.",
      course: "SVADS '25",
    },
    {
      quote: "The theory lectures were truly amazing. They were practical and insightful.",
      course: "AICDS '25",
    },
    {
      quote:
        "I have been dreaming of studying at ENTC since I was a child, but I missed out. I am fulfilling that desire because of this program.",
      course: "EPDIoT '25",
    },
  ],
};

const features = [
  {
    icon: industryOne,
    title: "Industry-backed content",
    text: "State-of-the-art content co-created with industry leaders.",
  },
  {
    icon: industryTwo,
    title: "Hands-on learning",
    text: "Hands-on learning coupled with strong fundamentals.",
  },
  {
    icon: industryThree,
    title: "Personalized feedback",
    text: "Personalized feedback to ensure no-one is left behind.",
  },
  {
    icon: industryFour,
    title: "Skilled instructors",
    text: "Taught by skilled teachers with a strong technical background.",
  },
];

const pillars = [
  {
    title: "Outcome Oriented",
    text: "There are two ways to learn a subject. Learn the concepts, and then figure out how to apply them. Or, start with the goal of creating something and learn the concepts to achieve that goal. We prefer the latter.",
  },
  {
    title: "Hands-On",
    text: "Throughout the course, our instructors work with you in real time, help you when you are stuck and improve iteratively. Mastering something is always an iterative, hands-on process, and each student has their own approach.",
  },
  {
    title: "Strong Fundamentals",
    text: "While abstraction is essential, what differentiates a highly skilled engineer is an in-depth understanding of the internals. A strong grasp of fundamentals is critical in achieving this goal.",
  },
];

const focusAreas = [
  { icon: chipIllustrationOne, label: "Internet of Things" },
  { icon: chipIllustrationTwo, label: "Computer Architecture" },
  { icon: shieldIllustration, label: "Cybersecurity" },
];

const steps = [
  {
    icon: "bi-search",
    title: "Choose a program",
    text: "Browse short courses, workshops and on-demand courses in the Program Catalog.",
  },
  {
    icon: "bi-credit-card",
    title: "Register online",
    text: "Secure your seat with our online registration and payment.",
  },
  {
    icon: "bi-tools",
    title: "Learn hands-on",
    text: "Build real projects with instructors who help you when you're stuck.",
  },
  {
    icon: "bi-award",
    title: "Get certified",
    text: "Earn a Skill Surf certificate when you complete the program.",
  },
];

const partners = [
  { src: synopsysLogo, alt: "Synopsys", href: "https://www.synopsys.com/" },
  { src: ieee_logo, alt: "IEEE Sri Lanka Section", href: "https://ieee.lk/" },
  { src: casLogo, alt: "IEEE CAS and CEDA" },
  { src: ttLogo, alt: "Tiny Tapeout", large: true },
];

const Home: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailInvalidText, setEmailInvalidText] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [subscriptionSuccessful, setSubscriptionSuccessful] = useState(false);
  const [emailExist, setEmailExist] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [cardInfo] = useState(cardData);
  const [show, setShow] = useState(false);

  const stats = getStats(cardInfo);
  const onDemandCourses = cardInfo.filter((course) => course.onDemand);
  const openCourse = cardInfo.find((course) =>
    course.currentState?.toLowerCase().includes("open")
  );

  const validate = () => {
    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!email || !regex.test(email)) {
      if (email) {
        setEmailInvalidText("Your email is invalid");
      } else {
        setEmailInvalidText("Your email is required");
      }
      setSubscriptionSuccessful(false);
      setEmailExist(false);
      return false;
    } else {
      return true;
    }
  };

  const handleSubmit = (e: any) => {
    setShowSpinner(true);
    e.preventDefault();
    const formData = new FormData(e.target);
    if (validate()) {
      setInvalidEmail(false);
      const url =
        "https://us-central1-skill-surf.cloudfunctions.net/api/subscribe";
      fetch(url, {
        method: "POST",
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Success:", data);
          setShowSpinner(false);
          setEmail("");
          if (data.message === "Subscribed successfully") {
            setSubscriptionSuccessful(true);
            setShow(true);
            setEmailExist(false);
            setShowSpinner(false);
          } else if (data.message === "Email already exist") {
            setEmailExist(true);
            setSubscriptionSuccessful(false);
            setShow(true);
            setShowSpinner(false);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          setSubscriptionSuccessful(false);
          setShow(true);
          setEmailExist(false);
          setShowSpinner(false);
        });
    } else {
      setInvalidEmail(true);
      setShow(true);
      setShowSpinner(false);
    }
  };

  const handleClose = () => setShow(false);

  const modes = [
    {
      icon: "bi-calendar-event",
      title: "Short Courses",
      count: `${stats.shortCourses} programs`,
      text: "Instructor-led programs, typically 8 weeks, that combine strong fundamentals with hands-on projects.",
      link: { to: "/program-catalog", label: "View short courses" },
    },
    {
      icon: "bi-lightning",
      title: "Workshops",
      count: `${stats.workshops} workshops`,
      text: "Intensive one- to four-day sessions to get started in a new area, from quantum computing to SoC design.",
      link: { to: "/program-catalog", label: "View workshops" },
    },
    {
      icon: "bi-play-circle",
      title: "On-Demand",
      count: `${stats.onDemand} courses`,
      text: "Self-paced versions of our most popular courses on Learnova, so you can learn on your own schedule.",
      link: { href: LEARNOVA_CATALOG, label: "Open Learnova" },
    },
  ];

  return (
    <div className="home-container ss-home fade-in">
      <NavBar />

      {/* Hero */}
      <section className="ss-hero">
        <img className="ss-hero__bg" src={heroImage} alt="" aria-hidden="true" />
        <div className="ss-hero__overlay" />
        <div className="ss-container ss-hero__content">
          <div className={`ss-hero__grid ${openCourse ? "has-enroll" : ""}`}>
            <div className="ss-hero__main">
              <h1>
                Industry&#8209;ready skills in chip design, quantum computing,
                electronics, embedded systems &amp; AI
              </h1>
              <p className="ss-hero__lead">
                Empowering all our learners with technical and life skills to make
                them industry and life&#8209;ready.
              </p>
              <div className="ss-hero__actions">
                <Link to="/program-catalog" className="ss-btn ss-btn--accent">
                  Explore Programs <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </Link>
                <a
                  href={LEARNOVA_CATALOG}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ss-btn ss-btn--ghost-light"
                >
                  Learn on-demand
                </a>
              </div>
            </div>
            {openCourse && (
              <aside className="ss-enroll" aria-label="Now enrolling">
                <div className="ss-enroll__media">
                  <img src={openCourse.cardImage} alt="" aria-hidden="true" />
                  <span className="ss-enroll__badge">
                    <span className="ss-enroll__pulse" aria-hidden="true"></span>
                    {openCourse.enrollmentDeadline ? "Enrollment open" : "Now enrolling"}
                  </span>
                </div>
                <div className="ss-enroll__body">
                  {openCourse.newIntakeText && (
                    <p className="ss-enroll__intake">
                      {openCourse.newIntakeText.split(" · ")[0]}
                    </p>
                  )}
                  <h2>{openCourse.title}</h2>
                  <ul className="ss-enroll__facts">
                    {openCourse.startDate && (
                      <li>
                        <i className="bi bi-calendar-event" aria-hidden="true"></i>
                        Starts {openCourse.startDate}
                      </li>
                    )}
                    {openCourse.duration && (
                      <li>
                        <i className="bi bi-clock" aria-hidden="true"></i>
                        {openCourse.duration}
                      </li>
                    )}
                    {openCourse.registrationDeadline && (
                      <li className="ss-enroll__deadline">
                        <i className="bi bi-hourglass-split" aria-hidden="true"></i>
                        Register by {openCourse.registrationDeadline}
                      </li>
                    )}
                    {openCourse.enrollmentDeadline && (
                      <>
                        <li className="ss-enroll__closed">
                          <i className="bi bi-x-circle" aria-hidden="true"></i>
                          Registration closed
                        </li>
                        <li className="ss-enroll__deadline">
                          <i className="bi bi-hourglass-split" aria-hidden="true"></i>
                          Enroll by {openCourse.enrollmentDeadline}
                        </li>
                      </>
                    )}
                  </ul>
                  {openCourse.enrollmentDeadline && (
                    <p className="ss-enroll__note">
                      Enrollment is open to registered participants only.
                    </p>
                  )}
                  <div className="ss-enroll__actions">
                    {openCourse.enrollLink && (
                      <a
                        href={openCourse.enrollLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ss-btn ss-btn--accent"
                      >
                        Enroll Now
                      </a>
                    )}
                    {openCourse.registerLink && (
                      <a
                        href={openCourse.registerLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ss-btn ss-btn--accent"
                      >
                        Register Now
                      </a>
                    )}
                    <Link
                      to={openCourse.actionProgramDetails}
                      className={`ss-btn ${
                        openCourse.registerLink || openCourse.enrollLink
                          ? "ss-btn--outline"
                          : "ss-btn--accent"
                      }`}
                    >
                      Course details
                    </Link>
                  </div>
                </div>
              </aside>
            )}
          </div>
          <ul className="ss-hero__stats">
            <li>
              <span className="ss-hero__stat-icon">
                <i className="bi bi-people-fill" aria-hidden="true"></i>
              </span>
              <span>
                <strong>{impact.studentsToDate}</strong>
                Students enrolled to date
              </span>
            </li>
            <li>
              <span className="ss-hero__stat-icon">
                <i className="bi bi-star-fill" aria-hidden="true"></i>
              </span>
              <span>
                <strong>{impact.avgRating}</strong>
                Average learner rating
              </span>
            </li>
            <li>
              <span className="ss-hero__stat-icon">
                <i className="bi bi-collection" aria-hidden="true"></i>
              </span>
              <span>
                <strong>{stats.programs}</strong>
                Courses &amp; workshops run
              </span>
            </li>
            <li>
              <span className="ss-hero__stat-icon">
                <i className="bi bi-calendar-check" aria-hidden="true"></i>
              </span>
              <span>
                <strong>2021</strong>
                Training engineers since
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* On-demand courses */}
      <section className="ss-section">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">On-Demand Courses</span>
            <h2>
              Start Learning <span>Today</span>
            </h2>
            <p>
              Self-paced versions of our highest-rated programs, available
              anytime on Learnova.
            </p>
          </div>
          <div className="ss-grid ss-grid--3">
            {onDemandCourses.map((course) => (
              <ProgramCard
                key={course.title}
                course={course}
                {...(course.onDemandLink?.startsWith("/")
                  ? { cta: "View course" }
                  : {
                      href: course.onDemandLink ?? LEARNOVA_CATALOG,
                      cta: "Start on Learnova",
                    })}
              />
            ))}
          </div>
          <div className="ss-center mt-5">
            <Link to="/program-catalog" className="ss-btn ss-btn--outline">
              See all programs <i className="bi bi-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Learning modes */}
      <section className="ss-section ss-section--tint">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">Ways to Learn</span>
            <h2>
              Choose the Format That <span>Works for You</span>
            </h2>
            <p>From intensive workshops to self-paced study.</p>
          </div>
          <div className="ss-grid ss-grid--3">
            {modes.map((mode) => (
              <div className="ss-mode" key={mode.title}>
                <span className="ss-mode__icon">
                  <i className={`bi ${mode.icon}`} aria-hidden="true"></i>
                </span>
                <h3>{mode.title}</h3>
                <p className="ss-mode__count">{mode.count}</p>
                <p>{mode.text}</p>
                {"to" in mode.link ? (
                  <Link to={mode.link.to!} className="ss-mode__link">
                    {mode.link.label} <i className="bi bi-arrow-right" aria-hidden="true"></i>
                  </Link>
                ) : (
                  <a
                    href={mode.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ss-mode__link"
                  >
                    {mode.link.label} <i className="bi bi-arrow-right" aria-hidden="true"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div className="ss-center mt-5">
            <Link to="/pathways" className="ss-btn ss-btn--outline">
              <i className="bi bi-signpost-split" aria-hidden="true"></i>
              Not sure where to start? See the learning pathways
            </Link>
          </div>
        </div>
      </section>

      {/* Why Skill Surf */}
      <section className="ss-section">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">Why Skill Surf</span>
            <h2>
              Become Industry &amp; <span>Life&#8209;Ready</span>
            </h2>
            <p>
              Our team spends months planning every course to deliver an
              outcome-oriented program that combines strong fundamentals with
              hands-on experience.
            </p>
          </div>
          <div className="ss-grid ss-grid--4">
            {features.map((feature) => (
              <div className="ss-feature" key={feature.title}>
                <img src={feature.icon} alt="" aria-hidden="true" />
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="ss-section ss-section--tint">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">{impact.year} Impact</span>
            <h2>
              A Year of <span>Firsts</span>
            </h2>
            <p>
              {impact.enrolledThisYear} learners enrolled in {impact.year},
              bringing our total to {impact.studentsToDate}.
            </p>
          </div>
          <div className="ss-grid ss-grid--3">
            {impact.highlights.map((item) => (
              <div className="ss-highlight" key={item.title}>
                <span className="ss-highlight__icon">
                  <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="ss-quotes__title">What our learners say</h3>
          <div className="ss-grid ss-grid--4">
            {impact.feedback.map((item) => (
              <figure className="ss-quote" key={item.course}>
                <i className="bi bi-quote" aria-hidden="true"></i>
                <blockquote>{item.quote}</blockquote>
                <figcaption>{item.course}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="ss-philosophy">
        <div className="ss-container ss-philosophy__inner">
          <div className="ss-philosophy__copy">
            <span className="ss-eyebrow ss-eyebrow--light">Our Approach</span>
            <h2>Our Talent Enhancement Philosophy</h2>
            <div className="ss-philosophy__pillars">
              {pillars.map((pillar, i) => (
                <div className="ss-pillar" key={pillar.title}>
                  <span className="ss-pillar__num">0{i + 1}</span>
                  <div>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="ss-philosophy__art">
            <img
              src={legoIllustration}
              alt="Illustration representing our talent enhancement philosophy"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section className="ss-section">
        <div className="ss-container ss-focus">
          <div className="ss-focus__copy">
            <span className="ss-eyebrow">Focus Areas</span>
            <h2>
              Future&#8209;proof <span>technologies</span>
            </h2>
            <p>
              We focus on future-proof technologies at the forefront of
              Industry 4.0, and we keep expanding our scope as we develop more
              content. But we are not in a hurry to expand at the expense of
              quality. Good things take time!
            </p>
          </div>
          <ul className="ss-focus__list">
            {focusAreas.map((area) => (
              <li key={area.label}>
                <img src={area.icon} alt="" aria-hidden="true" />
                {area.label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Journey */}
      <section className="ss-section ss-section--tint">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">How It Works</span>
            <h2>
              Your Journey Is <span>4 Steps Away</span>
            </h2>
          </div>
          <ol className="ss-steps">
            {steps.map((step, i) => (
              <li className="ss-step" key={step.title}>
                <span className="ss-step__icon">
                  <i className={`bi ${step.icon}`} aria-hidden="true"></i>
                </span>
                <span className="ss-step__num">Step {i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Partners */}
      <section className="ss-section ss-section--tight">
        <div className="ss-container">
          <div className="ss-section-head">
            <span className="ss-eyebrow">Friends &amp; Allies</span>
            <h2>
              Working <span>Together</span>
            </h2>
          </div>
          <ul className="ss-partners">
            {partners.map((partner) => {
              const logo = (
                <img
                  src={partner.src}
                  alt={partner.alt}
                  loading="lazy"
                  className={partner.large ? "is-large" : ""}
                />
              );
              return (
                <li key={partner.alt}>
                  {partner.href ? (
                    <a href={partner.href} target="_blank" rel="noopener noreferrer">
                      {logo}
                    </a>
                  ) : (
                    logo
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* CTA + subscribe */}
      <section className="ss-cta">
        <div className="ss-container">
          <div className="ss-cta__card">
            <div className="ss-cta__copy">
              <h2>Accelerate your career with job&#8209;ready skills</h2>
              <p>
                Get comprehensive training within 8 weeks, including a
                completion certificate from Skill Surf.
              </p>
              <Link to="/program-catalog" className="ss-btn ss-btn--accent">
                Explore Programs <i className="bi bi-arrow-right" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="ss-cta__subscribe">
              <h3>Get email updates about upcoming courses</h3>
              <form noValidate onSubmit={handleSubmit} className="ss-subscribe">
                <label htmlFor="subscribe-email" className="visually-hidden">
                  Email address
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  autoComplete="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                  }}
                />
                <button type="submit" className="ss-btn ss-btn--primary">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        <Modal show={show} onHide={handleClose} centered>
          <Modal.Body className="d-flex flex-column">
            {subscriptionSuccessful || emailExist || invalidEmail ? (
              <div className="bg-white d-flex gap-3 px-3 rounded-pill mb-3 justify-content-center">
                {subscriptionSuccessful && (
                  <img src={successIcon} alt="Success icon" />
                )}
                {emailExist && <img src={errorIcon} alt="Error icon" />}
                {invalidEmail && <img src={errorIcon} alt="Error icon" />}
                <p className="font-family-class text-gray mb-0">
                  {subscriptionSuccessful
                    ? "Thank You! Your email address has been added to the mailing list."
                    : emailExist
                      ? "Your email already exist in our mailing list!"
                      : emailInvalidText}
                </p>
              </div>
            ) : (
              <></>
            )}
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShow(false);
                }}
              >
                Okay
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </section>
      <Spinner showSpinner={showSpinner} />
      <Footer isFinancialInquiries={true} />
    </div>
  );
};

export default Home;
